import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Download, FileSpreadsheet, FileText, Search, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const ESTADO_LABELS = { nuevo:'Nuevo', en_atencion:'En Atención', resuelto:'Resuelto', cerrado:'Cerrado', falsa_alarma:'Falsa Alarma' };

export default function ConsultasPage() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [pagination, setPagination] = useState({ total:0, page:1, pages:1 });
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [tiposEvento, setTiposEvento] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [exporting, setExporting] = useState('');

  const [filters, setFilters] = useState({
    estado:'', prioridad:'', cliente_id:'', monitorista_id:'',
    tipo_evento_id:'', vehiculo_id:'', fecha_inicio:'', fecha_fin:'',
    page:1, limit:50
  });

  useEffect(() => {
    Promise.all([
      api.get('/catalogos/clientes?activo=true'),
      api.get('/catalogos/tipos-evento'),
      api.get('/auth/usuarios'),
      api.get('/catalogos/vehiculos?activo=true')
    ]).then(([c,te,u,v]) => {
      setClientes(c.data.data); setTiposEvento(te.data.data);
      setUsuarios(u.data.data); setVehiculos(v.data.data);
    }).catch(()=>{});
  }, []);

  const buscar = async (page = 1) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries({...filters, page}).filter(([,v]) => v !== ''));
      const res = await api.get('/eventos', { params });
      setEventos(res.data.data);
      setPagination(res.data.pagination);
    } catch(e) { toast.error('Error al consultar'); }
    finally { setLoading(false); }
  };

  const exportar = async (tipo) => {
    setExporting(tipo);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([,v]) => v !== '' && v !== 1 && v !== 50));
      const res = await api.get(`/exportar/${tipo}`, { params, responseType: 'blob' });
      const ext = tipo === 'excel' ? 'xlsx' : 'pdf';
      const mime = tipo === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = `eventos_${Date.now()}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado a ${ext.toUpperCase()}`);
    } catch(e) { toast.error('Error al exportar'); }
    finally { setExporting(''); }
  };

  const setFilter = (k, v) => setFilters(f => ({...f, [k]: v}));
  const limpiar = () => setFilters({ estado:'', prioridad:'', cliente_id:'', monitorista_id:'', tipo_evento_id:'', vehiculo_id:'', fecha_inicio:'', fecha_fin:'', page:1, limit:50 });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Consultas de Eventos</div>
          <div className="page-subtitle">Búsqueda avanzada y exportación</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => exportar('excel')} disabled={!!exporting}>
            {exporting==='excel' ? <span className="spinner" style={{width:14,height:14}}/> : <FileSpreadsheet size={14}/>} Excel
          </button>
          <button className="btn btn-secondary" onClick={() => exportar('pdf')} disabled={!!exporting}>
            {exporting==='pdf' ? <span className="spinner" style={{width:14,height:14}}/> : <FileText size={14}/>} PDF
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header"><span className="card-title">Filtros de Búsqueda</span></div>
        <div className="card-body">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:10 }}>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Estado</label>
              <select className="form-control" value={filters.estado} onChange={e => setFilter('estado', e.target.value)}>
                <option value="">Todos</option>
                <option value="nuevo">Nuevos (Sin atender)</option>
                <option value="en_atencion">En Atención</option>
                <option value="resuelto">Resueltos</option>
                <option value="cerrado">Cerrados</option>
                <option value="falsa_alarma">Falsa Alarma</option>
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Prioridad</label>
              <select className="form-control" value={filters.prioridad} onChange={e => setFilter('prioridad', e.target.value)}>
                <option value="">Todas</option>
                <option value="critica">Crítica</option>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Cliente</label>
              <select className="form-control" value={filters.cliente_id} onChange={e => setFilter('cliente_id', e.target.value)}>
                <option value="">Todos</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Tipo de Evento</label>
              <select className="form-control" value={filters.tipo_evento_id} onChange={e => setFilter('tipo_evento_id', e.target.value)}>
                <option value="">Todos</option>
                {tiposEvento.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Monitorista</label>
              <select className="form-control" value={filters.monitorista_id} onChange={e => setFilter('monitorista_id', e.target.value)}>
                <option value="">Todos</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>)}
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Vehículo</label>
              <select className="form-control" value={filters.vehiculo_id} onChange={e => setFilter('vehiculo_id', e.target.value)}>
                <option value="">Todos</option>
                {vehiculos.map(v => <option key={v.id} value={v.id}>{v.numero_economico}</option>)}
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Fecha Inicio</label>
              <input className="form-control" type="date" value={filters.fecha_inicio} onChange={e => setFilter('fecha_inicio', e.target.value)}/>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Fecha Fin</label>
              <input className="form-control" type="date" value={filters.fecha_fin} onChange={e => setFilter('fecha_fin', e.target.value)}/>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Resultados por pág.</label>
              <select className="form-control" value={filters.limit} onChange={e => setFilter('limit', e.target.value)}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={() => buscar(1)} disabled={loading}>
              {loading ? <span className="spinner" style={{width:14,height:14}}/> : <Search size={14}/>} Buscar
            </button>
            <button className="btn btn-secondary" onClick={limpiar}><RefreshCw size={14}/> Limpiar</button>
          </div>
        </div>
      </div>

      {/* Resultados */}
      {eventos.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Resultados</span>
            <span className="tag">{pagination.total} eventos</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Folio</th><th>Fecha Alerta</th><th>Tipo</th><th>Prioridad</th>
                  <th>Estado</th><th>Vehículo</th><th>Cliente</th><th>Monitorista</th>
                  <th>F. Atención</th><th>F. Cierre</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {eventos.map(ev => (
                  <tr key={ev.id}>
                    <td><span className="font-mono text-accent" style={{fontSize:11}}>{ev.folio}</span></td>
                    <td style={{fontSize:11,whiteSpace:'nowrap'}}>{ev.fecha_alerta ? format(new Date(ev.fecha_alerta),'dd/MM/yy HH:mm',{locale:es}) : '—'}</td>
                    <td style={{fontSize:12, maxWidth:140}}>
                      <span style={{display:'flex',alignItems:'center',gap:6}}>
                        {ev.tipoEvento?.color && <span style={{width:8,height:8,borderRadius:'50%',background:ev.tipoEvento.color,flexShrink:0}}/>}
                        {ev.tipoEvento?.nombre||'N/A'}
                      </span>
                    </td>
                    <td><span className={`badge badge-${ev.prioridad}`}>{ev.prioridad}</span></td>
                    <td><span className={`badge badge-${ev.estado}`}>{ESTADO_LABELS[ev.estado]}</span></td>
                    <td><span className="tag">{ev.vehiculo?.numero_economico||'N/A'}</span></td>
                    <td style={{fontSize:12}}>{ev.cliente?.nombre||'N/A'}</td>
                    <td style={{fontSize:12}}>{ev.monitorista?`${ev.monitorista.nombre} ${ev.monitorista.apellido}`:<span className="text-muted">—</span>}</td>
                    <td style={{fontSize:11,whiteSpace:'nowrap'}}>{ev.fecha_atencion?format(new Date(ev.fecha_atencion),'dd/MM/yy HH:mm',{locale:es}):'—'}</td>
                    <td style={{fontSize:11,whiteSpace:'nowrap'}}>{ev.fecha_cierre?format(new Date(ev.fecha_cierre),'dd/MM/yy HH:mm',{locale:es}):'—'}</td>
                    <td>
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/eventos/${ev.id}`)}>Ver</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => {
                          setExporting(`ficha_${ev.id}`);
                          api.get(`/exportar/ficha/${ev.id}`, { responseType: 'blob' })
                            .then(res => {
                              const url = URL.createObjectURL(new Blob([res.data],{type:'application/pdf'}));
                              const a = document.createElement('a'); a.href=url; a.download=`ficha_${ev.folio}.pdf`; a.click(); URL.revokeObjectURL(url);
                            }).catch(()=>toast.error('Error')).finally(()=>setExporting(''));
                        }}>
                          {exporting===`ficha_${ev.id}` ? <span className="spinner" style={{width:10,height:10}}/> : <FileText size={11}/>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginación */}
          {pagination.pages > 1 && (
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderTop:'1px solid var(--border)'}}>
              <span style={{fontSize:12,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>Pág. {pagination.page} de {pagination.pages}</span>
              <div style={{display:'flex',gap:6}}>
                {Array.from({length:pagination.pages},(_, i)=>i+1).slice(0,10).map(p=>(
                  <button key={p} className={`btn btn-sm ${p===pagination.page?'btn-primary':'btn-secondary'}`} onClick={()=>buscar(p)}>{p}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && eventos.length === 0 && (
        <div className="card">
          <div className="table-empty">Usa los filtros y presiona Buscar para consultar eventos</div>
        </div>
      )}
    </div>
  );
}
